import { Box, Spinner } from '@chakra-ui/react';
import { PaginatedResponse, Params } from '@/pages/Bookstore/types';
import { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UseMutationOptions, UseMutationResult } from 'react-query';
import { useTheme } from '@/pages/Bookstore/contexts/BookstoreThemeAdapter';

const order = [
  'Época Negócios',
  'Galileu',
  'Pequenas Empresas & Grandes Negócios',
  'Marie Claire',
  'Auto Esporte',
  'Globo Rural',
];

export function InfiniteScroll<T>({
  mutationHook,
  renderItem,
  emptyResultsComponent = <></>,
  keyExtraction,
  children,
  filterMagazine = false,
  requireCover = true,
  hookProps = {},
}: IInfiniteScrollProps<T>) {
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { colors } = useTheme();
  const [resultDict, setResultDict] = useState<Record<number, T[]>>({});
  const isLoadingRef = useRef(false);

  const serializedHookProps = useMemo(() => JSON.stringify(hookProps ?? {}), [hookProps]);
  const [filtersKey, setFiltersKey] = useState(serializedHookProps);

  const mutation = mutationHook({
    onSuccess(data) {
      isLoadingRef.current = false;

      setResultDict((prevDict) => {
        const newDict = page === 1 ? {} : { ...prevDict };

        let ordenerdResults = (
          requireCover
            ? data.results.filter((item) => {
                const i = item as Record<string, unknown>;
                const book = i.book as Record<string, unknown> | undefined;
                return book?.cover || book?.coverOptimized || i.cover || i.coverOptimized;
              })
            : data.results
        ) as T[];

        if (filterMagazine) {
          const grouped: Record<string, T[]> = {};
          data.results.forEach((item) => {
            const name = (item as { name?: string }).name || '';
            if (!grouped[name]) grouped[name] = [];
            grouped[name].push(item);
          });

          ordenerdResults = [];
          let added = true;
          while (added) {
            added = false;
            for (const magazine of order) {
              if (grouped[magazine] && grouped[magazine].length > 0) {
                ordenerdResults.push(grouped[magazine].shift() as T);
                added = true;
              }
            }
          }
        }

        newDict[page] = [...ordenerdResults];
        return newDict;
      });

      const nextPage = 'next' in data ? (data as { next?: string | null }).next : undefined;
      setHasNextPage(Boolean(nextPage));
      setIsInitialLoading(false);
    },
    onError() {
      isLoadingRef.current = false;
      setIsInitialLoading(false);
      // Roll back the failed page so the next scroll retries it
      // instead of advancing past a gap with stale data.
      setPage((prev) => (prev > 1 ? prev - 1 : prev));
    },
  });

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const requestNextPage = useCallback(() => {
    if (isLoadingRef.current || !hasNextPage) return;
    isLoadingRef.current = true;
    setPage((prev) => prev + 1);
  }, [hasNextPage]);

  // IntersectionObserver works regardless of which element actually scrolls
  // (window vs. body vs. inner container). Avoids the scrollY/scrollHeight
  // mismatch caused by `html, body { height: 100% }` in index.css.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) requestNextPage();
      },
      { rootMargin: '400px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, requestNextPage]);

  // Reset state when filters change
  useEffect(() => {
    if (filtersKey === serializedHookProps) return;

    setFiltersKey(serializedHookProps);
    setResultDict({});
    setHasNextPage(false);
    setPage(1);
    setIsInitialLoading(true);
  }, [serializedHookProps, filtersKey]);

  // Fetch data when page or filters change
  useEffect(() => {
    mutation.mutate({ page, ...hookProps });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filtersKey]);

  const results = Object.values(resultDict).reduce<T[]>((acc, current = []) => [...acc, ...current], []);

  if (isInitialLoading)
    return (
      <Box w={'100%'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
        <Spinner color={colors.primaryColor} size={'lg'} />
      </Box>
    );

  if (!mutation.isLoading && results.length === 0) return emptyResultsComponent;

  return (
    <>
      {children(results.map((r: T) => cloneElement(renderItem(r), { key: keyExtraction(r) })))}
      {mutation.isLoading && (
        <Box w="100%" display="flex" justifyContent="center" py={4}>
          <Spinner color={colors.primaryColor} size="md" />
        </Box>
      )}
      <Box ref={sentinelRef} w="100%" h="1px" />
    </>
  );
}

interface IInfiniteScrollProps<T> {
  mutationHook: (
    options: UseMutationOptions<PaginatedResponse<T>, unknown, Params, unknown>
  ) => UseMutationResult<PaginatedResponse<T>, unknown, Params, unknown>;
  renderItem: (item: T) => React.JSX.Element;
  children: (items: React.JSX.Element[]) => React.JSX.Element;
  hookProps?: Params;
  keyExtraction: (t: T) => string;
  emptyResultsComponent?: React.JSX.Element;
  filterMagazine?: boolean;
  requireCover?: boolean;
}
